import * as babylon from "@babylonjs/core";

/** Width multiplier at the base (fat end) of each vector triangle. */
export const VECTOR_FIELD_BASE_WIDTH = 8;

export interface GradientOptions {
  colorLow: babylon.Color3;
  colorHigh: babylon.Color3;
  minMagnitude: number;
  maxMagnitude: number;
}

/**
 * Creates a single greased line mesh containing disconnected line segments,
 * one per sampled point, showing the direction and magnitude of a vector
 * at each position along a path. Each segment is rendered as a triangle
 * with a fat base tapering to a point at the tip.
 */
export function createVectorFieldMesh(
  name: string,
  color: string,
  lineWidth: number,
  positions: babylon.Vector3[],
  vectors: babylon.Vector3[],
  stride: number,
  scene: babylon.Scene,
  gradient?: GradientOptions,
): babylon.Mesh {
  const segments: babylon.Vector3[][] = [];
  const widths: number[] = [];
  const colors: babylon.Color3[] = [];
  const defaultColor = babylon.Color3.FromHexString(color);

  for (let i = 0; i < positions.length && i < vectors.length; i += stride) {
    const start = positions[i];
    const end = start.add(vectors[i]);
    segments.push([start, end]);
    // Each segment has 2 points, each point gets a below/above width pair.
    // Base (start): fat, Tip (end): pointed.
    widths.push(VECTOR_FIELD_BASE_WIDTH, VECTOR_FIELD_BASE_WIDTH, 0, 0);

    if (gradient) {
      const range = gradient.maxMagnitude - gradient.minMagnitude;
      const t =
        range > 0 ? (vectors[i].length() - gradient.minMagnitude) / range : 0.5;
      const clamped = Math.max(0, Math.min(1, t));
      const segmentColor = babylon.Color3.Lerp(
        gradient.colorLow,
        gradient.colorHigh,
        clamped,
      );
      // One color per point in the segment.
      colors.push(segmentColor, segmentColor);
    }
  }

  return babylon.CreateGreasedLine(
    name,
    {
      points: segments,
      widths,
      widthDistribution:
        babylon.GreasedLineMeshWidthDistribution.WIDTH_DISTRIBUTION_NONE,
    },
    {
      color: defaultColor,
      width: lineWidth,
      sizeAttenuation: true,
      ...(gradient ? { useColors: true, colors } : {}),
    },
    scene,
  );
}
